import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QrCode, X } from 'lucide-react';
import CameraQrScanner from '../common/CameraQrScanner';
import { GiftRequest, giftQrError, scanGiftQr } from '../../services/giftQrService';
import './giftQr.css';
import AdminGiftStart from './AdminGiftStart';

export default function PosGiftQr({ sessionId, operatorToken, onRegistered }: {
    sessionId: number; operatorToken?: string | null; onRegistered: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scannerVersion, setScannerVersion] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<GiftRequest | null>(null);
    const [remaining, setRemaining] = useState(0);
    const inFlight = useRef(false);
    const dialog = useRef<HTMLDivElement>(null);
    const generation = useRef(0);
    useEffect(() => { setOpen(false); setResult(null); setScanning(false); generation.current += 1; }, [sessionId, operatorToken]);
    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement as HTMLElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden'; dialog.current?.focus();
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'Tab') {
                const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input') || []);
                if (controls.length && event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus(); }
                else if (controls.length && !event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => { generation.current += 1; document.body.style.overflow = overflow; document.removeEventListener('keydown', onKey); previous?.focus(); };
    }, [open]);
    useEffect(() => {
        if (!result?.consumo) return;
        const start = Date.parse(result.consumo.fecha_inicio);
        const server = Date.parse(result.servidor_ahora);
        const received = performance.now();
        const tick = () => setRemaining(Math.max(0, Math.ceil((start - server - (performance.now() - received)) / 1000)));
        tick(); const timer = window.setInterval(tick, 250);
        return () => clearInterval(timer);
    }, [result]);
    const register = useCallback(async (qr: string) => {
        if (inFlight.current || !operatorToken) return;
        inFlight.current = true; const version = generation.current;
        setScanning(false); setBusy(true); setError('');
        try {
            const data = await scanGiftQr(qr, sessionId, operatorToken);
            if (version !== generation.current) return;
            setResult(data); onRegistered();
        } catch (e) { if (version === generation.current) setError(giftQrError(e)); }
        finally { inFlight.current = false; setBusy(false); }
    }, [operatorToken, sessionId, onRegistered]);
    return <>
        <button className="rw-button" onClick={() => setManualOpen(true)}>Iniciar horas de regalo</button>
        <button className="rw-button" onClick={() => { setError(''); setResult(null); setOpen(true); }}><QrCode size={18} />Horas de regalo · QR</button>
        <AdminGiftStart key={`${sessionId}-${operatorToken}`} open={manualOpen} onClose={() => setManualOpen(false)} onStarted={onRegistered} sessionId={sessionId} operatorToken={operatorToken} />
        {open && createPortal(<div className="gift-qr-overlay" onMouseDown={event => { if (event.target === event.currentTarget) setOpen(false); }}>
            <div ref={dialog} className="gift-qr-dialog" role="dialog" aria-modal="true" aria-labelledby="pos-gift-qr-title" tabIndex={-1}>
                <header className="gift-qr-header"><div><h2 id="pos-gift-qr-title">Registrar horas de regalo</h2><p>Escanea el QR del cliente en la sucursal de este POS.</p></div><button aria-label="Cerrar lector de QR" onClick={() => setOpen(false)}><X size={20} /></button></header>
                {!operatorToken && <p className="gift-qr-info">Selecciona un operador e ingresa su PIN en el POS para registrar consumos.</p>}
                {error && <p role="alert" className="gift-qr-error">{error}</p>}
                {busy && <p role="status">Validando y registrando…</p>}
                {scanning && <CameraQrScanner key={scannerVersion} onScan={register} onError={setError} isActive={scanning && !busy} />}
                <div className="gift-qr-actions"><button className="gift-qr-primary" disabled={busy || !operatorToken} onClick={() => { setResult(null); setError(''); setScannerVersion(value => value + 1); setScanning(true); }}><QrCode size={18} />{scanning ? 'Reiniciar lector' : 'Escanear QR del cliente'}</button>{scanning && <button onClick={() => setScanning(false)}>Detener cámara</button>}</div>
                {result && <section className="gift-qr-status" style={{ marginTop: 20 }}><h3>{remaining ? `Registro confirmado · comienza en ${remaining} s` : result.consumo?.estado === 'en_curso' ? 'Consumo registrado' : 'Este QR ya fue registrado y finalizado'}</h3><dl className="gift-qr-summary">{[['Cliente', result.cliente], ['Sucursal', result.sucursal], ['Servicio', result.servicio], ['Espacio', result.espacio], ['Operador', result.operador || '—']].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl><p>{remaining ? 'El cliente dispone de 10 segundos desde el registro para llegar al espacio.' : 'Volver a escanear el mismo QR conserva el consumo original.'}</p></section>}
            </div>
        </div>, document.body)}
    </>;
}
